import { useEffect, useRef, useState } from 'react';
import type { ServiceLandingData } from '../../../mocks/service-landings';

interface SLProblemsProps {
  data: ServiceLandingData;
}

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function SLProblems({ data }: SLProblemsProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-900">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          {/* Header */}
          <div
            className={`text-center mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-alert-line text-orange-400 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Prečo to nefunguje</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
              {data.problems.heading}
            </h2>
            <p className="text-zinc-400 text-base max-w-xl mx-auto leading-relaxed">
              {data.problems.subheading}
            </p>
          </div>

          {/* Problem cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.problems.items.map((item, i) => (
              <div
                key={item.title}
                className={`relative bg-zinc-800/60 border border-zinc-700/60 rounded-2xl p-7 transition-all duration-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                {/* Red accent dot */}
                <div className="absolute top-5 right-5 w-2 h-2 rounded-full bg-red-500/60" />

                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 mb-5">
                  <i className={`${item.icon} text-2xl text-red-400`} />
                </div>
                <h3 className="text-white font-bold text-lg mb-3 leading-tight">{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Bottom note */}
          <div
            className={`mt-10 text-center transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <p className="text-zinc-500 text-sm">
              Každý z týchto problémov má riešenie - a začína sa jednou konzultáciou.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
